import React from "react";
import { BsChevronDown, BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { MdDelete } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { IoMdArrowDropdown } from "react-icons/io";

import { validatePicklistOption, getCounterColor } from "../../utils/fieldValidation";
import { FIELD_CHAR_LIMITS } from "../../constants/fieldCharLimits";
import {
  removeCoordinate,
  updateCoordinateMappingField,
  updateCoordinateOptions,
  updateCoordinateSignatory,
  updateCoordinateIsRequired,
} from "../../utils/updateCoordinates";
import { setPicklistCoordinates } from "../../redux/slices/picklistCoordinatesReducer";
import { setActiveCoordinate } from "../../redux/slices/activeCoordinateReducer";
import CustomSelect from "../../components/custom-select";
import CustomToggle from "../../components/custom-toggle";
import { SalesforceFieldTypeEnum } from "../../constants/salesforceFieldTypeEnum";

function SelectedPicklistContainer() {
  const dispatch = useDispatch();
  const activeCoordinate = useSelector(
    (state) => state.activeCoordinate.activeCoordinate
  );
  const signatoryList = useSelector(
    (state) => state.signatoryList.signatoryList
  );
  const picklistCoordinates = useSelector(
    (state) => state.picklistCoordinates.picklistCoordinates
  );
  const sobjectAPIName = useSelector(
    (state) => state.salesforceFieldsMetadata.sobjectAPIName
  );
  const salesforceFieldsMetadata = useSelector(
    (state) => state.salesforceFieldsMetadata.salesforceFieldsMetadata
  );

  const [inputValue, setInputValue] = useState("");
  const [currentCoordOptions, setCurrentCoordOptions] = useState([]);
  const [picklistName, setPicklistName] = useState("");
  const [picklistNameEdited, setPicklistNameEdited] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [dropdown, setDropdown] = useState(true);

  const normalizeOptions = (options) => {
    let opts = [];
    if (typeof options === "string") {
      opts = options.split(",");
    } else if (Array.isArray(options)) {
      opts = options;
    } else if (options != null) {
      opts = [options];
    }
    return opts
      .map((option) => (option || "").toString().trim())
      .filter((option) => option !== "");
  };

  const handleEleDelete = () => {
    const updatedCoords = removeCoordinate(
      picklistCoordinates,
      activeCoordinate.eleId
    );
    dispatch(setPicklistCoordinates({ picklistCoordinates: [] }));
    setTimeout(() => {
      dispatch(setPicklistCoordinates({ picklistCoordinates: updatedCoords }));
    }, 0);
    dispatch(
      setActiveCoordinate({ activeCoordinate: {}, isSelectionMode: false })
    );
  };

  const updateActiveCoordinateOptions = (optionsArray) => {
    const normalizedOptions = normalizeOptions(optionsArray);
    const optionsString = normalizedOptions.join(",");

    const updatedPicklistCoordinates = picklistCoordinates.map((item) => {
      if (item.eleId === activeCoordinate.eleId) {
        return {
          ...item,
          options: optionsString,
          name: picklistName,
        };
      }
      return item;
    });

    dispatch(
      setPicklistCoordinates({
        picklistCoordinates: updateCoordinateOptions(
          updatedPicklistCoordinates,
          activeCoordinate.eleId,
          optionsString
        ),
      })
    );

    dispatch(
      setActiveCoordinate({
        activeCoordinate: {
          ...activeCoordinate,
          value: "",
          options: optionsString,
          name: picklistName,
        },
        isSelectionMode: true,
      })
    );

    setPicklistNameEdited(false);
  };

  const handleCurrentCoordSignatoryChange = (e) => {
    dispatch(
      setPicklistCoordinates({
        picklistCoordinates: updateCoordinateSignatory(
          picklistCoordinates,
          activeCoordinate.eleId,
          e.id,
          e.index,
          e.bgColor
        ),
      })
    );

    const updatedActiveCoordinates = {
      ...activeCoordinate,
      value: "",
      signatory_id: e.id,
      user_index_id: e.index,
      options: currentCoordOptions.join(","),
      bgColor: e.bgColor,
    };

    dispatch(
      setActiveCoordinate({
        activeCoordinate: updatedActiveCoordinates,
        isSelectionMode: true,
      })
    );
  };

  const handleCurrentCoordIsRequiredChange = (value) => {
    dispatch(
      setPicklistCoordinates({
        picklistCoordinates: updateCoordinateIsRequired(
          picklistCoordinates,
          activeCoordinate.eleId,
          value
        ),
      })
    );

    dispatch(
      setActiveCoordinate({
        activeCoordinate: {
          ...activeCoordinate,
          isRequired: value,
        },
        isSelectionMode: true,
      })
    );
  };


  const handleCurrentCoordMappingFieldChange = (e) => {
    if (activeCoordinate.updateFromSalesforce || activeCoordinate.writeBack) {
      dispatch(
        setPicklistCoordinates({
          picklistCoordinates: updateCoordinateMappingField(
            picklistCoordinates,
            activeCoordinate.eleId,
            e
          ),
        })
      );

      dispatch(
        setActiveCoordinate({
          activeCoordinate: {
            ...activeCoordinate,
            mappingField: e,
            value: "",
          },
          isSelectionMode: true,
        })
      );
    }
  };

  const handleAddOrUpdate = (action, oldIndex) => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue === "") return;

    const optValidation = validatePicklistOption(trimmedValue);
    if (!optValidation.valid) {
      alert(optValidation.error);
      return;
    }

    const normalizedOptions = normalizeOptions(currentCoordOptions);
    if (action === "Add") {
      if (normalizedOptions.length >= FIELD_CHAR_LIMITS.PickList.maxOptions) {
        alert(`Maximum ${FIELD_CHAR_LIMITS.PickList.maxOptions} options allowed`);
        return;
      }
      const updatedOptions = [...normalizedOptions, trimmedValue];
      setCurrentCoordOptions(updatedOptions);
      updateActiveCoordinateOptions(updatedOptions);
      setInputValue("");
      setEditIndex(null);
      return;
    }

    if (action === "Update") {
      if (oldIndex === null || oldIndex === undefined) return;
      const updatedOptions = normalizedOptions.map((item, index) =>
        index === oldIndex ? trimmedValue : item
      );
      setCurrentCoordOptions(updatedOptions);
      updateActiveCoordinateOptions(updatedOptions);
      setInputValue("");
      setEditIndex(null);
    }
  };

  const handleEdit = (index) => {
    const normalizedOptions = normalizeOptions(currentCoordOptions);
    setInputValue(normalizedOptions[index] || "");
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    const updatedOptions = normalizeOptions(currentCoordOptions).filter(
      (_, i) => i !== index
    );
    setCurrentCoordOptions(updatedOptions);
    if (editIndex === index) {
      setEditIndex(null);
      setInputValue("");
    }
    updateActiveCoordinateOptions(updatedOptions);
  };

  const toggleDropdown = () => {
    setDropdown((prev) => !prev);
  };

  useEffect(() => {
    setCurrentCoordOptions(normalizeOptions(activeCoordinate.options));
    setPicklistName(activeCoordinate.name || "");
    setEditIndex(null);
    setInputValue("");
  }, [
    activeCoordinate.eleId,
    activeCoordinate.name,
    activeCoordinate.options,
    activeCoordinate.value,
  ]);

  return (
    <>
      <div className="right-sidebar-tool-detail-mobile">
        <div
          style={{ marginLeft: "30px" }}
          className="right-sidebar-tool-header"
        >
          <div style={{ alignSelf: "center" }}>
            <BsChevronDown
              style={{ color: "#555d65", margin: "0px 5px -3px 0px" }}
            />
            <span
              style={{
                fontSize: "12px",
                alignSelf: "center",
                color: "#555d65",
              }}
            >
              Picklist
            </span>
          </div>
          <MdDelete
            style={{
              cursor: "pointer",
              backgroundColor: "red",
              padding: "5px",
              borderRadius: "100%",
              fontSize: "16px",
              marginRight: "50px",
              color: "white",
            }}
            onClick={handleEleDelete}
          />
        </div>
        {dropdown && (
          <>
            <div className="right-sidebar-section">
              <label htmlFor="currentCoordSigner" className="field-lbl">
                Assigned to
              </label>
              <div>
                <CustomSelect
                  value={signatoryList.find(
                    (signatory) =>
                      signatory.id === activeCoordinate.signatory_id
                  )}
                  name="currentCoordSigner"
                  options={signatoryList}
                  onChange={(e) => {
                    handleCurrentCoordSignatoryChange(e);
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                paddingLeft: "0 !important",
              }}
              className="right-sidebar-section"
            >
              {activeCoordinate.updateFromSalesforce ||
              activeCoordinate.writeBack ? (
                <>
                  <hr style={{ width: "100vw", margin: "5px 0" }} />
                  <div id="textbox-input" className="right-sidebar-section">
                    <label className="field-lbl">
                      Map with {sobjectAPIName}'s field
                    </label>
                    <CustomSelect
                      value={activeCoordinate.mappingField}
                      name="signer"
                      options={salesforceFieldsMetadata.filter(
                        (field) =>
                          field.type === SalesforceFieldTypeEnum.PICKLIST
                      )}
                      useFor="MAPPING"
                      onChange={(e) => {
                        handleCurrentCoordMappingFieldChange(e);
                      }}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </>
        )}
        <div className="mobile-dropdown" style={{ marginTop: "4px" }}>
          <div onClick={toggleDropdown} className="dropdown-toggle-btn">
            <IoMdArrowDropdown
              style={{
                margin: "10px",
                marginTop: "16px",
                position: "absolute",
                top: "0",
                fontSize: "x-large",
                left: "0",
                padding: "0px",
                color: "black",
                borderRadius: "100%",
              }}
            />
            <hr />
          </div>
        </div>
      </div>
      <div className="right-sidebar-tool-detail-desktop">
        <div className="right-sidebar-tool-header">
          <div>
            <BsChevronDown
              style={{ color: "#555d65", margin: "0px 5px -3px 0px" }}
            />
            <span
              style={{
                fontSize: "16px",
                color: "#555d65",
              }}
            >
              Picklist
            </span>
          </div>
        </div>

        <div className="right-sidebar-section">
          <label htmlFor="currentCoordSigner" className="field-lbl">
            Assigned to
          </label>
          <div>
            <CustomSelect
              value={signatoryList.find(
                (signatory) => signatory.id === activeCoordinate.signatory_id
              )}
              name="currentCoordSigner"
              options={signatoryList}
              onChange={(e) => {
                handleCurrentCoordSignatoryChange(e);
              }}
            />
          </div>
        </div>

        {activeCoordinate.updateFromSalesforce || activeCoordinate.writeBack ? (
          <div className="right-sidebar-section">
            <label className="field-lbl">
              Map with {sobjectAPIName}'s field
            </label>
            <CustomSelect
              value={activeCoordinate.mappingField}
              name="signer"
              options={salesforceFieldsMetadata.filter(
                (field) =>
                  field.type === SalesforceFieldTypeEnum.PICKLIST
              )}
              useFor="MAPPING"
              onChange={(e) => {
                handleCurrentCoordMappingFieldChange(e);
              }}
            />
          </div>
        ) : null}
        {picklistNameEdited && (
          <div
            style={{
              position: "fixed",
              width: "100vw",
              height: "100vh",
              zIndex: "9999",
              left: "0",
              top: "0",
            }}
          >
            &nbsp;
          </div>
        )}

        {(!activeCoordinate.updateFromSalesforce &&
          !activeCoordinate.writeBack) && (
          <>
            <div key={activeCoordinate.value} className="right-sidebar-section">
              <label className="field-lbl">Set PickList</label>
              <div className="picklist-group">
                <input
                  className="input-field"
                  value={picklistName}
                  maxLength={FIELD_CHAR_LIMITS.PickList.nameMaxLength}
                  onChange={(e) => {
                    setPicklistName(
                      e.target.value.slice(
                        0,
                        FIELD_CHAR_LIMITS.PickList.nameMaxLength
                      )
                    );
                    setPicklistNameEdited(true);
                  }}
                  onBlur={() => {
                    updateActiveCoordinateOptions(currentCoordOptions);
                  }}
                  placeholder="Enter picklist name"
                />
                <span
                  className="char-counter"
                  style={{
                    color: getCounterColor(
                      picklistName.length,
                      FIELD_CHAR_LIMITS.PickList.nameMaxLength
                    ),
                  }}
                >
                  {picklistName.length}/{FIELD_CHAR_LIMITS.PickList.nameMaxLength}
                </span>

                <div className="picklist-input-row">
                  <input
                    className="input-field"
                    value={inputValue}
                    maxLength={FIELD_CHAR_LIMITS.PickList.optionMaxLength}
                    onChange={(e) => {
                      setInputValue(
                        e.target.value.slice(
                          0,
                          FIELD_CHAR_LIMITS.PickList.optionMaxLength
                        )
                      );
                    }}
                    placeholder="Enter picklist value"
                  />
                  <button
                    className="add-update-btn"
                    onClick={() =>
                      handleAddOrUpdate(
                        editIndex !== null ? "Update" : "Add",
                        editIndex
                      )
                    }
                  >
                    {editIndex !== null ? "Update" : "Add"}
                  </button>
                </div>
                <span
                  className="char-counter"
                  style={{
                    color: getCounterColor(
                      inputValue.length,
                      FIELD_CHAR_LIMITS.PickList.optionMaxLength
                    ),
                  }}
                >
                  {inputValue.length}/{FIELD_CHAR_LIMITS.PickList.optionMaxLength}
                </span>

                <ul className="picklist-ul">
                  {normalizeOptions(currentCoordOptions).map((item, index) => (
                    <li className="picklist-li" key={index}>
                      <span className="picklist-text" title={item}>
                        {item.length > 10 ? `${item.slice(0, 10)}...` : item}
                      </span>
                      <div className="picklist-actions">
                        <button
                          className="picklist-btn"
                          onClick={() => handleEdit(index)}
                        >
                          <BsPencilSquare />
                        </button>
                        <button
                          className="picklist-btn"
                          onClick={() => handleDelete(index)}
                        >
                          <BsTrashFill />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                {normalizeOptions(currentCoordOptions).length === 0 && (
                  <div
                    style={{
                      color: "#e53e3e",
                      fontSize: "12px",
                      fontWeight: 600,
                      padding: "6px 10px",
                      marginTop: "6px",
                      border: "1px solid #e53e3e",
                      borderRadius: "4px",
                      backgroundColor: "rgba(229, 62, 62, 0.08)",
                    }}
                  >
                    ⚠ Add at least one option
                  </div>
                )}
              </div>
            </div>

            {activeCoordinate.mappingField?.type ===
              SalesforceFieldTypeEnum.PICKLIST && (
              <div className="right-sidebar-section">
                <label className="field-lbl">Default PickList</label>
                <CustomSelect
                  value={{
                    label: activeCoordinate.value,
                    value: activeCoordinate.value,
                  }}
                  name="picklistValue"
                  options={activeCoordinate.mappingField.picklistOption}
                  useFor="MAPPING"
                  onChange={() => {
                    updateActiveCoordinateOptions(currentCoordOptions);
                  }}
                />
              </div>
            )}
          </>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
          className="right-sidebar-section"
        >
          <CustomToggle
            title="Required"
            defaultValue={activeCoordinate.isRequired}
            onChange={(value) => {
              handleCurrentCoordIsRequiredChange(value);
            }}
          />

          <button
            onClick={handleEleDelete}
            style={{
              cursor: "pointer",
              fontSize: "small",
              marginRight: "0px",
              color: "#ffffff",
              backgroundColor: "#4d4d4d",
              borderRadius: "5px",
              position: "absolute",
              bottom: "-50px",
              border: "0",
              shadown: "0",
              alignItems: "center",
              alignSelf: "center",
              right: "50%",
              padding: "8px",
              marginTop: "10px",
              width: "40%",
              display: "flex",
              zIndex: "0",
            }}
          >
            <MdDelete />
            DELETE
          </button>
        </div>
      </div>
    </>
  );
}

export default SelectedPicklistContainer;
